import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { useSocket } from '../../contexts/SocketContext';

const BroadcastOutput = () => {
  const { id, type } = useParams();
  const { socket, connected, joinOverlay, leaveOverlay } = useSocket();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [rotatingTextIndex, setRotatingTextIndex] = useState(0);
  const [textFade, setTextFade] = useState(false);
  const [showUTC, setShowUTC] = useState(true);
  const [memberScroll, setMemberScroll] = useState([]);
  const [currentCommentIndex, setCurrentCommentIndex] = useState(0);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const [superchatVisible, setSuperchatVisible] = useState(false);
  const [superchatAnimating, setSuperchatAnimating] = useState(false);
  const [commentVisible, setCommentVisible] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupAnimating, setPopupAnimating] = useState(false);
  const [currentPopupId, setCurrentPopupId] = useState(null);
  const [popupChanging, setPopupChanging] = useState(false);
  const [scrollDuration, setScrollDuration] = useState(1200); // Base slow duration in seconds
  const [headlineVisible, setHeadlineVisible] = useState(false);
  const [headline3Visible, setHeadline3Visible] = useState(false);
  const [updateBannerVisible, setUpdateBannerVisible] = useState(false);

  // Calculate scroll duration based on member count to maintain constant speed
  useEffect(() => {
    if (memberScroll.length > 0) {
      // Base duration: 1200s (slower than previous 900s)
      // Auto-adjust: multiply by member count factor to keep same speed as members are added
      // Formula: baseDuration * (1 + memberCount * 0.15) ensures smooth scaling
      const calculatedDuration = 1200 * (1 + memberScroll.length * 0.15);
      setScrollDuration(calculatedDuration);
    } else {
      // Default slow speed when no members
      setScrollDuration(1200);
    }
  }, [memberScroll.length]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      setCurrentTime(new Date());
      if (!connected) {
        fetchData();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [id, type, connected]);

  // Comment rotation effect - change comment every minute
  useEffect(() => {
    if (data?.comments && data.comments.length > 0) {
      // Reset to first comment when new comments are loaded
      setCurrentCommentIndex(0);

      const commentInterval = setInterval(() => {
        setCurrentCommentIndex(prev => (prev + 1) % data.comments.length);
      }, 60000); // 60 seconds = 1 minute

      return () => clearInterval(commentInterval);
    }
  }, [data?.comments]);

  // Superchat and regular comment animation effect - push left (slide in from right), pull right (slide out to right)
  useEffect(() => {
    if (data?.comments && data.comments.length > 0 && data.controlState?.commentsVisible && data.controlState?.chatConnected) {
      const currentComment = data.comments[currentCommentIndex];
      const isSuperchat = currentComment?.amountMicros && currentComment.amountMicros > 0;

      if (isSuperchat) {
        // Hide popup and regular comments if superchat is showing (mutual exclusivity)
        setPopupVisible(false);
        setCommentVisible(false);

        // Show superchat - CSS transition will handle smooth slide in from right
        setSuperchatVisible(true);
      } else {
        // Hide superchat - CSS transition will handle smooth slide out to right
        setSuperchatVisible(false);

        // Show regular comment if popup is not visible
        if (!popupVisible) {
          setCommentVisible(true);
        } else {
          setCommentVisible(false);
        }
      }
    } else {
      setSuperchatVisible(false);
      setCommentVisible(false);
    }
  }, [data?.comments, currentCommentIndex, data?.controlState?.commentsVisible, data?.controlState?.chatConnected, popupVisible]);

  // Update banner animation effect - slide in from left to right
  useEffect(() => {
    const shouldShowBanner = data?.overlay?.updateType && data?.controlState?.updateBannerVisible;

    if (shouldShowBanner) {
      // Small delay to ensure smooth animation on initial load
      const timer = setTimeout(() => {
        setUpdateBannerVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Hide banner with animation
      setUpdateBannerVisible(false);
    }
  }, [data?.overlay?.updateType, data?.controlState?.updateBannerVisible]);

  // Headline animation effect - slide in from left to right
  useEffect(() => {
    const shouldShowHeadlines = data?.overlay && (data.overlay.headline1 || data.overlay.subHeadline) &&
      (data.controlState?.headerVisible !== false || data.controlState?.subheaderVisible !== false);

    if (shouldShowHeadlines) {
      // Small delay to ensure smooth animation on initial load
      const timer = setTimeout(() => {
        setHeadlineVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Hide headline with animation
      setHeadlineVisible(false);
    }
  }, [data?.overlay?.headline1, data?.overlay?.subHeadline, data?.controlState?.headerVisible, data?.controlState?.subheaderVisible]);

  // Headline 3 animation effect - slide in from left to right
  useEffect(() => {
    const shouldShowHeadline3 = data?.overlay && data.overlay.headline3 && data.controlState?.header3Visible !== false;

    if (shouldShowHeadline3) {
      // Small delay to ensure smooth animation on initial load
      const timer = setTimeout(() => {
        setHeadline3Visible(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Hide headline 3 with animation
      setHeadline3Visible(false);
    }
  }, [data?.overlay?.headline3, data?.controlState?.header3Visible]);

  // Popup animation effect - push left (slide in from right), pull right (slide out to right)
  // Popup, superchat, and comments are mutually exclusive - only one can show at a time
  useEffect(() => {
    const activePopupId = data?.activePopups && data.activePopups.length > 0 ? data.activePopups[0].id : null;

    if (activePopupId && !superchatVisible && !commentVisible) {
      // Check if popup is changing to a different one
      if (currentPopupId && currentPopupId !== activePopupId) {
        // Popup is changing - animate out old, then animate in new
        setPopupVisible(false);
        setCommentVisible(false); // Hide comments when popup is showing
        const timeoutId = setTimeout(() => {
          setCurrentPopupId(activePopupId);
          setPopupVisible(true);
        }, 1200); // Wait for slide out animation (matches transition duration)
        return () => clearTimeout(timeoutId);
      } else {
        // Same popup or new popup (no previous) - show immediately
        if (currentPopupId !== activePopupId) {
          setCurrentPopupId(activePopupId);
        }
        setPopupVisible(true);
        setCommentVisible(false); // Hide comments when popup is showing
      }
    } else {
      // Hide popup - CSS transition will handle smooth slide out to right
      setPopupVisible(false);
      if (!activePopupId) {
        setCurrentPopupId(null);
      }
    }
  }, [data?.activePopups, superchatVisible, commentVisible, currentPopupId]);

  // Refresh member scroll data periodically to get updates
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      console.log('Auto-refreshing broadcast data...');
      fetchData();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(refreshInterval);
  }, [id, type]);

  // Refresh data when window regains focus (user switches back to broadcast)
  useEffect(() => {
    const handleFocus = () => {
      console.log('Window focused - refreshing broadcast data...');
      fetchData();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [id, type]);

  // Text rotation effect with fade animation
  useEffect(() => {
    const rotationInterval = setInterval(() => {
      // Fade out
      setTextFade(true);

      // After fade out completes, change text and fade in
      setTimeout(() => {
        setRotatingTextIndex(prevIndex => (prevIndex + 1) % 3);
        setTextFade(false);
      }, 500); // Half second fade transition
    }, 20000); // 20 seconds

    return () => clearInterval(rotationInterval);
  }, []);

  // Clock rotation between UTC and launch site timezone
  useEffect(() => {
    const clockRotationInterval = setInterval(() => {
      setShowUTC(prev => !prev);
    }, 10000); // 10 seconds

    return () => clearInterval(clockRotationInterval);
  }, []);

  useEffect(() => {
    if (connected && id) {
      joinOverlay(id);
      return () => leaveOverlay(id);
    }
  }, [connected, id, joinOverlay, leaveOverlay]);

  useEffect(() => {
    if (socket) {
      const handleOverlayUpdate = (updateData) => {
        console.log('🔧 Broadcast Output - Received WebSocket update:', updateData);

        try {
          if (updateData.type === 'control-update') {
            setData(prevData => {
              if (!prevData) {
                console.warn('⚠️ No previous data available for control-update');
                return prevData;
              }
              return {
                ...prevData,
                controlState: { ...prevData.controlState, ...updateData.data }
              };
            });
          } else if (updateData.type === 'event-update') {
            setData(prevData => {
              if (!prevData || !prevData.events) {
                console.warn('⚠️ No previous data or events available for event-update');
                return prevData;
              }
              return {
                ...prevData,
                events: prevData.events.map(event =>
                  event.id === updateData.eventId ? { ...event, ...updateData.data } : event
                )
              };
            });
          } else if (updateData.type === 'popup-update') {
            console.log('🔧 Popup update received, updating active popups...');
            setData(prevData => {
              if (!prevData) {
                console.warn('⚠️ No previous data available for popup-update');
                return prevData;
              }
              return {
                ...prevData,
                activePopups: updateData.data.activePopups || []
              };
            });
          } else if (updateData.type === 'countdown-update') {
            setData(prevData => {
              if (!prevData) {
                console.warn('⚠️ No previous data available for countdown-update');
                return prevData;
              }
              return {
                ...prevData,
                countdown: updateData.data.countdown,
                windowCountdown: updateData.data.windowCountdown
              };
            });
          } else if (updateData.type === 'comment-update') {
            setData(prevData => {
              if (!prevData) {
                console.warn('⚠️ No previous data available for comment-update');
                return prevData;
              }
              return {
                ...prevData,
                comments: [updateData.data, ...(prevData.comments || []).slice(0, 9)]
              };
            });
          } else if (updateData.type === 'overlay-update') {
            console.log('🔧 Overlay update received, refreshing full data to ensure sync...');
            // Refresh full data to ensure all fields are in sync
            fetchData();
          } else if (updateData.type === 'member-scroll-update') {
            setMemberScroll(updateData.data.members || []);
          } else if (updateData.type === 'member-updated') {
            // Handle individual member updates
            setMemberScroll(prev => prev.map(member =>
              member.id === updateData.member.id ? updateData.member : member
            ));
          } else if (updateData.type === 'member-created') {
            // Handle new member creation
            setMemberScroll(prev => [...prev, updateData.member]);
          } else if (updateData.type === 'member-deleted') {
            // Handle member deletion
            setMemberScroll(prev => prev.filter(member => member.id !== updateData.memberId));
          }
        } catch (error) {
          console.error('❌ Error handling WebSocket update:', error, updateData);
          // Fallback: refresh data if WebSocket update fails
          console.log('🔄 Refreshing broadcast data due to WebSocket error...');
          fetchData();
        }
      };

      socket.on('overlay-updated', handleOverlayUpdate);
      socket.on('countdown-updated', handleOverlayUpdate);
      socket.on('comment-added', handleOverlayUpdate);
      socket.on('member-scroll-update', handleOverlayUpdate);

      return () => {
        socket.off('overlay-updated', handleOverlayUpdate);
        socket.off('countdown-updated', handleOverlayUpdate);
        socket.off('comment-added', handleOverlayUpdate);
        socket.off('member-scroll-update', handleOverlayUpdate);
      };
    }
  }, [socket]);

  const fetchData = async () => {
    try {
      let response;
      if (type === 'overlay') {
        response = await api.get(`/broadcast/overlay/${id}`);
      } else if (type === 'chat') {
        response = await api.get(`/broadcast/chat/${id}`);
      } else {
        response = await api.get(`/broadcast/overlay/${id}`);
      }
      console.log('Broadcast data received:', response.data);
      console.log('Comments in response:', response.data.comments);
      console.log('Headline3 in response:', response.data.overlay?.headline3);
      setData(response.data);
      setLastRefreshTime(Date.now());

      // Set member scroll data from the broadcast response
      if (response.data.memberScroll) {
        console.log('Broadcast Output - Received member scroll data:', response.data.memberScroll);
        setMemberScroll(response.data.memberScroll || []);
      }
    } catch (error) {
      console.error('Failed to fetch broadcast data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date) => {
    return date.toISOString().substr(11, 8);
  };

  // Format time in a specific timezone
  const formatTimeInTimezone = (date, timezone) => {
    if (!timezone || timezone === 'UTC') {
      return date.toISOString().substr(11, 8) + ' UTC';
    }

    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      const parts = formatter.formatToParts(date);
      const hour = parts.find(p => p.type === 'hour')?.value || '00';
      const minute = parts.find(p => p.type === 'minute')?.value || '00';
      const second = parts.find(p => p.type === 'second')?.value || '00';

      // Get timezone abbreviation
      const tzFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      });
      const tzParts = tzFormatter.formatToParts(date);
      const tzName = tzParts.find(p => p.type === 'timeZoneName')?.value || timezone.split('/').pop()?.toUpperCase() || 'LOCAL';

      return `${hour}:${minute}:${second} ${tzName}`;
    } catch (error) {
      console.error('Error formatting timezone:', error);
      return date.toISOString().substr(11, 8) + ' UTC';
    }
  };

  const formatCountdown = (countdown) => {
    if (!countdown) return '00:00:00:00';
    const { days, hours, minutes, seconds } = countdown;
    return `${days.toString().padStart(2, '0')}:${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatWindowCountdown = (countdown) => {
    if (!countdown) return '00:00:00';
    const { hours, minutes, seconds } = countdown;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Real-time countdown calculation with pause functionality for all non-GO states
  const calculateRealTimeCountdown = () => {
    if (!data?.overlay?.t0Timestamp) return null;

    const now = currentTime;
    const t0Time = new Date(data.overlay.t0Timestamp);

    // Apply stream delay to T-0 time (add delay seconds to T-0)
    const streamDelay = data.controlState?.streamDelay || 0;
    const adjustedT0Time = new Date(t0Time.getTime() + (streamDelay * 1000));

    // If status is not GO and not DEFAULT, pause the countdown by using the paused time
    if (data.controlState?.countdownState && data.controlState?.countdownState !== 'GO' && data.controlState?.countdownState !== 'DEFAULT') {
      // Use the paused time from control state if available
      const pausedAt = data.controlState?.countdownPausedAt ? new Date(data.controlState.countdownPausedAt) : now;
      const pausedDuration = data.controlState?.countdownPausedDuration || 0;

      // Calculate time remaining from when it was paused (using adjusted T-0)
      const originalDiffMs = adjustedT0Time.getTime() - pausedAt.getTime();
      const adjustedDiffMs = originalDiffMs + (pausedDuration * 1000);

      if (adjustedDiffMs <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

      const days = Math.floor(adjustedDiffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((adjustedDiffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((adjustedDiffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((adjustedDiffMs % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds };
    }

    // Normal countdown calculation (using adjusted T-0)
    const diffMs = adjustedT0Time.getTime() - now.getTime();

    // For DEFAULT state, always show countdown (even if T-0 has passed, show absolute value like a normal clock)
    const isDefault = data.controlState?.countdownState === 'DEFAULT';
    if (isDefault) {
      // Show absolute value - always display countdown like a normal clock
      const absDiffMs = Math.abs(diffMs);
      const days = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((absDiffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((absDiffMs % (1000 * 60)) / 1000);
      return { days, hours, minutes, seconds };
    }

    // For other states, show zeros if T-0 has passed
    if (diffMs <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  };

  // Real-time window countdown calculation
  const calculateRealTimeWindowCountdown = () => {
    if (!data?.overlay?.windowClose) return null;

    const now = currentTime;
    const windowCloseTime = new Date(data.overlay.windowClose);
    const diffMs = windowCloseTime.getTime() - now.getTime();

    if (diffMs <= 0) return { hours: 0, minutes: 0, seconds: 0 };

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    return { hours, minutes, seconds };
  };

  const formatEventTime = (offset) => {
    if (!offset) return '';
    const totalSeconds = Math.abs(offset);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Real-time event time calculation that ticks down
  const calculateEventTime = (event) => {
    if (!event.timeOffset || !data?.overlay?.t0Timestamp) return '';

    const now = currentTime;
    const t0Time = new Date(data.overlay.t0Timestamp);

    // Apply stream delay to T-0 time (add delay seconds to T-0)
    const streamDelay = data.controlState?.streamDelay || 0;
    const adjustedT0Time = new Date(t0Time.getTime() + (streamDelay * 1000));

    // Calculate the actual event time based on adjusted T-0 and offset
    // timeOffset: negative for T- events, positive for T+ events
    const eventTime = new Date(adjustedT0Time.getTime() + (event.timeOffset * 1000));

    // If status is not GO and not DEFAULT, pause the countdown by using the paused time
    let diffMs;
    if (data.controlState?.countdownState && data.controlState?.countdownState !== 'GO' && data.controlState?.countdownState !== 'DEFAULT') {
      // Use the paused time from control state if available
      const pausedAt = data.controlState?.countdownPausedAt ? new Date(data.controlState.countdownPausedAt) : now;
      const pausedDuration = data.controlState?.countdownPausedDuration || 0;

      // Calculate time difference from when it was paused
      const originalDiffMs = eventTime.getTime() - pausedAt.getTime();
      diffMs = originalDiffMs + (pausedDuration * 1000);
    } else {
      // Normal calculation for GO state
      diffMs = eventTime.getTime() - now.getTime();
    }

    if (diffMs <= 0) return '00:00';

    const totalSeconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Real-time countdown from timeOffset (seconds) with proper formatting
  const displayEventTime = (event) => {
    // Handle T-0 events (timeOffset === -999999) - show main countdown
    if (event?.timeOffset === -999999) {
      const realTimeCountdown = calculateRealTimeCountdown();
      if (!realTimeCountdown) return '00:00';

      const { days, hours, minutes, seconds } = realTimeCountdown;
      const totalSeconds = days * 86400 + hours * 3600 + minutes * 60 + seconds;

      // Format as MM:SS or HH:MM:SS based on total time
      if (totalSeconds >= 3600) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      } else {
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
    }

    // Handle regular events with timeOffset (including 0)
    if (event?.timeOffset === undefined || event?.timeOffset === -999999 || !data?.overlay?.t0Timestamp) return '00:00';

    const now = currentTime;
    const t0Time = new Date(data.overlay.t0Timestamp);

    // Apply stream delay to T-0 time (add delay seconds to T-0)
    const streamDelay = data.controlState?.streamDelay || 0;
    const adjustedT0Time = new Date(t0Time.getTime() + (streamDelay * 1000));

    // Calculate the actual event time based on adjusted T-0 and offset
    // timeOffset: negative for T- events, positive for T+ events
    const eventTime = new Date(adjustedT0Time.getTime() + (event.timeOffset * 1000));

    // If status is not GO and not DEFAULT, pause the countdown by using the paused time
    let diffMs;
    if (data.controlState?.countdownState && data.controlState?.countdownState !== 'GO' && data.controlState?.countdownState !== 'DEFAULT') {
      // Use the paused time from control state if available
      const pausedAt = data.controlState?.countdownPausedAt ? new Date(data.controlState.countdownPausedAt) : now;
      const pausedDuration = data.controlState?.countdownPausedDuration || 0;

      // Calculate time difference from when it was paused
      const originalDiffMs = eventTime.getTime() - pausedAt.getTime();
      diffMs = originalDiffMs + (pausedDuration * 1000);
    } else {
      // Normal calculation for GO state
      diffMs = eventTime.getTime() - now.getTime();
    }

    // If event has passed, start counting up from T-0
    if (diffMs <= 0) {
      const elapsedMs = Math.abs(diffMs);
      const totalSeconds = Math.floor(elapsedMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      // Format as +MM:SS or +HH:MM:SS
      if (hours > 0) {
        return `+${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      } else {
        return `+${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
    }

    // Convert to proper format based on hours
    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Format rule: If hours > 0 → show hh:mm:ss, If hours = 0 → show only mm:ss
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
  };


  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
        <div className="text-white text-center">
          <div className="spinner w-12 h-12 mx-auto mb-4 border-white border-4 border-t-blue-400 rounded-full animate-spin"></div>
          <p>Loading broadcast data...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="w-full h-screen flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
        <div className="text-white text-center">
          <p className="text-2xl">No broadcast data available</p>
        </div>
      </div>
    );
  }

  // Validate data structure to prevent blank broadcast
  if (!data.overlay || !data.controlState) {
    const now = Date.now();
    if (now - lastRefreshTime > 5000) { // Only refresh if more than 5 seconds have passed
      console.warn('⚠️ Invalid data structure detected, refreshing...');
      setLastRefreshTime(now);
      fetchData();
    }
    return (
      <div className="w-full h-screen flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
        <div className="text-white text-center">
          <h2 className="text-2xl mb-4">Refreshing Data...</h2>
          <p>Please wait while we reload the broadcast data.</p>
        </div>
      </div>
    );
  }

  // Emergency mode check
  const isEmergencyMode = data?.controlState?.emergencyMode === true

  // Helper function to check if element touches edges
  const touchesEdge = (position) => {
    if (!isEmergencyMode) return true // Show all in normal mode
    // Check if element is at top, bottom, left, or right edge (within 4px)
    const edgeThreshold = 4
    return (
      position.top <= edgeThreshold ||
      position.bottom >= window.innerHeight - edgeThreshold ||
      position.left <= edgeThreshold ||
      position.right >= window.innerWidth - edgeThreshold
    )
  }

  // Helper function to apply emergency styling
  const getEmergencyStyle = (originalStyle = {}) => {
    if (!isEmergencyMode) return originalStyle

    return {
      ...originalStyle,
      backgroundColor: originalStyle.backgroundColor ? '#2a2a2a' : undefined,
      color: '#2a2a2a',
      borderColor: originalStyle.borderColor ? '#2a2a2a' : undefined,
    }
  }

  // Helper function to apply emergency classes
  const getEmergencyClasses = (originalClasses = '') => {
    if (!isEmergencyMode) return originalClasses

    // Replace color classes with dark grey equivalents
    return originalClasses
      .replace(/bg-\[#[^\]]+\]/g, 'bg-[#2a2a2a]')
      .replace(/bg-red-\d+/g, 'bg-[#2a2a2a]')
      .replace(/bg-green-\d+/g, 'bg-[#2a2a2a]')
      .replace(/bg-blue-\d+/g, 'bg-[#2a2a2a]')
      .replace(/bg-yellow-\d+/g, 'bg-[#2a2a2a]')
      .replace(/bg-orange-\d+/g, 'bg-[#2a2a2a]')
      .replace(/text-red-\d+/g, 'text-[#2a2a2a]')
      .replace(/text-green-\d+/g, 'text-[#2a2a2a]')
      .replace(/text-blue-\d+/g, 'text-[#2a2a2a]')
      .replace(/text-yellow-\d+/g, 'text-[#2a2a2a]')
      .replace(/text-orange-\d+/g, 'text-[#2a2a2a]')
      .replace(/border-red-\d+/g, 'border-[#2a2a2a]')
      .replace(/border-green-\d+/g, 'border-[#2a2a2a]')
      .replace(/border-blue-\d+/g, 'border-[#2a2a2a]')
      .replace(/border-yellow-\d+/g, 'border-[#2a2a2a]')
      .replace(/border-orange-\d+/g, 'border-[#2a2a2a]')
  }

  return (
    <div className="w-full h-screen relative overflow-hidden" style={{ backgroundColor: 'transparent' }}>
      {/* Top Left - Live Status & Time with Headline 3 - Always visible (touches top-left edge) */}
      <div className={`absolute top-4 left-0 z-10 ${isEmergencyMode ? 'opacity-50' : ''}`}>
        <div className="flex items-start gap-4">
          <div style={{ width: 'fit-content' }}>
            <div className={`flex items-stretch border-b-2 ${isEmergencyMode ? 'border-[#2a2a2a]' : 'border-[#a20000]'}`} style={{ width: 'fit-content' }}>
              <div className={`${isEmergencyMode ? 'bg-[#2a2a2a]' : 'bg-[#a20000]'} px-3 py-0.5 text-white text-2xl flex-shrink-0 font-nasalization flex items-center justify-center`}>
                LIVE
              </div>
              <div className="bg-black px-3 py-0.5 text-white text-xl flex-shrink-0 text-center whitespace-nowrap flex items-center justify-center" style={{ minWidth: '140px' }}>
                {showUTC
                  ? `${currentTime.toISOString().substr(11, 8)} UTC`
                  : formatTimeInTimezone(currentTime, data.overlay?.timezone || 'UTC')
                }
              </div>
            </div>
            {data.overlay && data.overlay.facility && (
              <div className="bg-black px-3 text-white text-sm flex items-center justify-center" style={{ width: '100%' }}>
                {data.overlay.facility}
              </div>
            )}
          </div>

          {/* Headline 3 - Displayed as continuous bar to the right of live status/time */}
          {data.overlay && data.overlay.headline3 && data.controlState?.header3Visible !== false && (
            <div
              className={`bg-black bg-opacity-75 px-4 py-1 text-white text-lg font-medium flex items-center uppercase ${headline3Visible
                ? 'translate-x-0 opacity-100'
                : '-translate-x-full opacity-0'
                }`}
              style={{
                width: 'fit-content',
                maxWidth: 'calc(100vw - 500px)',
                alignSelf: 'flex-start',
                marginLeft: '8px',
                pointerEvents: headline3Visible ? 'auto' : 'none',
                transition: 'transform 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              }}
            >
              {data.overlay.headline3}
            </div>
          )}
        </div>
      </div>

      {/* Left Side - Countdown Timeline (Vertical View) - Visible in emergency mode (touches left edge) */}
      {(!isEmergencyMode || true) && data.controlState?.eventsViewType !== 'horizontal' && (
        <div className={`absolute left-2 top-20 bottom-24 z-10 ${isEmergencyMode ? 'opacity-50' : ''}`} style={{ width: 'fit-content' }}>
          <div className="py-1 flex justify-end" >



            {/* Timeline Events */}
            <div className="relative">


              {/* Events List */}
              <div className=" space-y-1">
                {(() => {
                  // Calculate maximum event name length to make all bars uniform
                  const visibleEvents = data.events
                    .filter(event => event.isVisible)
                    .sort((a, b) => a.sortOrder - b.sortOrder);
                  
                  const maxEventNameLength = Math.max(
                    ...visibleEvents.map(event => (event.eventName || 'Unnamed Event').length),
                    0
                  );
                  
                  // Add 8-10 characters worth of extra width (approximately 8-10px per character for text-sm)
                  // Using 9 characters as a middle ground
                  const extraChars = 9;
                  const estimatedCharWidth = 8; // Approximate width per character in pixels for text-sm
                  const maxEventNameWidth = (maxEventNameLength + extraChars) * estimatedCharWidth;
                  
                  return visibleEvents.map((event, index, array) => {
                    // Check if event has reached 0:00 (completed) - use EXACT same logic as displayEventTime
                    let diffMs = null;
                    let totalSeconds = null;
                    // Handle T-0 events (timeOffset === -999999) - use main countdown logic
                    if (event?.timeOffset === -999999 && data?.overlay?.t0Timestamp) {
                      const realTimeCountdown = calculateRealTimeCountdown();
                      if (realTimeCountdown) {
                        const { days, hours, minutes, seconds } = realTimeCountdown;
                        totalSeconds = days * 86400 + hours * 3600 + minutes * 60 + seconds;
                        // For T-0 events, show green when countdown reaches 0
                        diffMs = totalSeconds === 0 ? 0 : (totalSeconds > 0 ? 1 : -1);
                      }
                    } else if (event?.timeOffset !== undefined && data?.overlay?.t0Timestamp) {
                      const now = currentTime;
                      const t0Time = new Date(data.overlay.t0Timestamp);
                      const streamDelay = data.controlState?.streamDelay || 0;
                      const adjustedT0Time = new Date(t0Time.getTime() + (streamDelay * 1000));
                      const eventTime = new Date(adjustedT0Time.getTime() + (event.timeOffset * 1000));

                      // Check if countdown has reached 0:00 or passed - use EXACT same logic as displayEventTime
                      diffMs = eventTime.getTime() - now.getTime();
                      if (data.controlState?.countdownState && data.controlState?.countdownState !== 'GO' && data.controlState?.countdownState !== 'DEFAULT') {
                        const pausedAt = data.controlState?.countdownPausedAt ? new Date(data.controlState.countdownPausedAt) : now;
                        const pausedDuration = data.controlState?.countdownPausedDuration || 0;
                        const originalDiffMs = eventTime.getTime() - pausedAt.getTime();
                        diffMs = originalDiffMs + (pausedDuration * 1000);
                      }

                      // Calculate total seconds (EXACT same as displayEventTime does)
                      // When displayEventTime shows "00:00" (without plus), diffMs is 0-999ms, so totalSeconds = 0
                      // When displayEventTime shows "+MM:SS", diffMs <= 0
                      totalSeconds = Math.floor(diffMs / 1000);
                    }

                    // Calculate width to match LIVE/time box: LIVE box (~60px) + time box (140px minWidth + padding) ≈ 211px
                    const liveTimeBoxWidth = 211; // Matches the LIVE/time box width
                    
                    // Calculate new total width: clock (60px) + gap (8px) + max event name width
                    const newTotalWidth = 60 + 8 + maxEventNameWidth;

                    // Determine if event should show green bar (completed)
                    // Green if: 1) Manually marked complete in admin, OR 2) Event has reached 0:00 or passed
                    // When displayEventTime shows "00:00" (without plus), diffMs is 0-999ms, so totalSeconds = 0
                    // When displayEventTime shows "+MM:SS", diffMs <= 0
                    // So green if: diffMs <= 0 (passed) OR totalSeconds <= 0 (at 0:00)
                    // The key: when totalSeconds = 0, it means diffMs is 0-999ms, which shows as "00:00"
                    const shouldShowGreen = event.isComplete || (diffMs !== null && diffMs <= 0) || (totalSeconds !== null && totalSeconds <= 0);

                    return (
                      <div key={event.id} className="flex items-center h-8 gap-2" style={{ width: `${Math.max(liveTimeBoxWidth, newTotalWidth)}px` }}>
                        {/* Clock/Timestamp on LEFT - Always render div, transparent when hidden - Fixed size */}
                        <div className={`px-2 py-2 text-sm text-white flex items-center justify-center h-full ${event.hasClock ? 'bg-black bg-opacity-75' : 'bg-transparent'}`} style={{ minWidth: '60px', width: '60px' }}>
                          {event.hasClock ? displayEventTime(event) : ''}
                        </div>

                        {/* Event Name on RIGHT - Separate div with green/grey vertical bar on left edge */}
                        <div className="bg-black bg-opacity-75 px-2 py-2 text-sm font-medium text-white flex items-center h-full relative" style={{ minWidth: `${maxEventNameWidth}px`, width: `${maxEventNameWidth}px` }}>
                          {/* Green/grey vertical bar on left edge - 2x thickness (4px), green only if completed */}
                          <div className={`absolute left-0 top-0 bottom-0 w-[4px] ${isEmergencyMode ? 'bg-[#2a2a2a]' : shouldShowGreen ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                          <span className="ml-2 uppercase whitespace-nowrap">{event.eventName || 'Unnamed Event'}</span>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Right - Mission & Countdown - Visible in emergency mode (touches top-right edge) */}
      {(!isEmergencyMode || true) && data.overlay && (
        <div className={`absolute top-4 right-0 z-10 flex gap-4 ${isEmergencyMode ? 'opacity-50' : ''}`}>
          <div className={`min-w-[200px] max-w-[240px] w-fit`}>
            {/* Secondary Window Countdown - End of Launch Window */}
            {data.overlay?.windowClose && data.controlState?.launchWindowVisible && data.controlState?.secondaryClockClose && (
              <div>
                {/* Black Header Bar - WINDOW CLOSES */}
                <div className="bg-black text-white flex items-center" style={{ width: '100%' }}>
                  <div className="flex-1 px-3 text-center text-lg font-medium flex items-center justify-center">
                    <span className="truncate whitespace-nowrap">WINDOW CLOSES</span>
                  </div>
                </div>

                {/* Red Countdown Box */}
                <div className={`${isEmergencyMode ? 'bg-[#2a2a2a]' : 'bg-[#a20000]'} text-white text-center flex items-center justify-center px-4`} style={{ width: '100%' }}>
                  <div className="text-[24px] text-white flex justify-center gap-2" style={{ paddingTop: '4px' }}>
                    {(() => {
                      const windowCountdown = calculateRealTimeWindowCountdown();
                      if (!windowCountdown) return '00:00:00';

                      const { hours, minutes, seconds } = windowCountdown;
                      return (
                        <>
                          <div className="text-center">
                            <div className="text-[24px]" style={{ height: 'auto', lineHeight: 1, padding: 0, margin: 0 }}>{hours.toString().padStart(2, '0')}</div>
                            <div className="text-sm text-gray-300">H</div>
                          </div>
                          <div className="text-[24px]">:</div>
                          <div className="text-center">
                            <div className="text-[24px]" style={{ height: 'auto', lineHeight: 1, padding: 0, margin: 0 }}>{minutes.toString().padStart(2, '0')}</div>
                            <div className="text-sm text-gray-300">M</div>
                          </div>
                          <div className="text-[24px]">:</div>
                          <div className="text-center">
                            <div className="text-[24px]" style={{ height: 'auto', lineHeight: 1, padding: 0, margin: 0 }}>{seconds.toString().padStart(2, '0')}</div>
                            <div className="text-sm text-gray-300">S</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Countdown Section - Only show if primary countdown is visible */}
          {data.controlState?.primaryCountdownVisible && (
            <div className={`min-w-[200px] w-fit flex flex-col ${(!data.controlState?.countdownState || data.controlState?.countdownState === 'DEFAULT') ? 'gap-0' : ''}`}>
              {/* Black Header Bar - NAME and MISSION */}
              <div className="bg-black text-white flex items-center w-full">
                {/* NAME Section - Equal width, centered */}
                <div className="px-3 text-center text-lg font-medium flex items-center justify-center flex-1">
                  <span className="whitespace-nowrap">{data.overlay.title || 'NAME'}</span>
                </div>
                {/* Vertical Divider - Divides the two sections but doesn't need to be centered */}
                <div className={`${isEmergencyMode ? 'bg-[#2a2a2a]' : 'bg-[#a20000]'} flex-shrink-0 self-stretch`} style={{ width: '2px' }}></div>
                {/* MISSION Section - Equal width, centered */}
                <div className="px-3 text-center text-lg font-medium flex items-center justify-center flex-1">
                  <span className="whitespace-nowrap">{data.overlay.missionName || 'MISSION'}</span>
                </div>
              </div>

              {/* Red Countdown Box - Expands to match full container width */}
              {/* Reduce padding when no status bar (DEFAULT state) to position clock higher */}
              {(() => {
                const hasStatusBar = data.controlState?.countdownState && data.controlState?.countdownState !== 'DEFAULT';
                return (
                  <div className={`${isEmergencyMode ? 'bg-[#2a2a2a]' : 'bg-[#a20000]'} text-white text-center flex items-center justify-center px-4 w-full`} style={{
                    minHeight: 'auto',
                    paddingTop: hasStatusBar ? '1px' : '4px',
                    paddingBottom: hasStatusBar ? '1px' : '4px'
                  }}>
                    {data.controlState?.primaryCountdownVisible ? (
                      <div className="text-[24px] text-white flex justify-center gap-2" style={{ paddingTop: hasStatusBar ? '4px' : '0px' }}>
                        {(() => {
                          const realTimeCountdown = calculateRealTimeCountdown();
                          if (!realTimeCountdown) return '00:00:00:00';

                          const { days, hours, minutes, seconds } = realTimeCountdown;
                          // Always show labels (Day, Hour, Min, Sec) - DEFAULT state should show labels too
                          const showLabels = true;
                          const showDays = data.controlState?.showDays;
                          const showHours = data.controlState?.showHours !== false; // Default to true if not set

                          if (showDays) {
                            // Show days, and conditionally show hours
                            return (
                              <>
                                <div className="text-center flex flex-col items-center">
                                  <div className="text-[24px]" style={{ height: 'auto', lineHeight: 1, padding: 0, margin: 0 }}>{days.toString().padStart(2, '0')}</div>
                                  {showLabels && <div className="text-sm text-gray-300">Day</div>}
                                </div>
                                {showHours && (
                                  <>
                                    <div className="text-[24px] flex items-start" style={{ paddingTop: 0, lineHeight: 1 }}>:</div>
                                    <div className="text-center flex flex-col items-center">
                                      <div className="text-[24px]" style={{ height: 'auto', lineHeight: 1, padding: 0, margin: 0 }}>{hours.toString().padStart(2, '0')}</div>
                                      {showLabels && <div className="text-sm text-gray-300">Hour</div>}
                                    </div>
                                  </>
                                )}
                                <div className="text-[24px] flex items-start" style={{ paddingTop: 0, lineHeight: 1 }}>:</div>
                                <div className="text-center flex flex-col items-center">
                                  <div className="text-[24px]" style={{ height: 'auto', lineHeight: 1, padding: 0, margin: 0 }}>{minutes.toString().padStart(2, '0')}</div>
                                  {showLabels && <div className="text-sm text-gray-300">Min</div>}
                                </div>
                                <div className="text-[24px] flex items-start" style={{ paddingTop: 0, lineHeight: 1 }}>:</div>
                                <div className="text-center flex flex-col items-center">
                                  <div className="text-[24px]" style={{ height: 'auto', lineHeight: 1, padding: 0, margin: 0 }}>{seconds.toString().padStart(2, '0')}</div>
                                  {showLabels && <div className="text-sm text-gray-300">Sec</div>}
                                </div>
                              </>
                            );
                          } else if (showHours) {
                            // Show hours, minutes, seconds (no days)
                            return (
                              <>
                                <div className="text-center flex flex-col items-center">
                                  <div className="text-[24px]" style={{ height: 'auto', lineHeight: 1, padding: 0, margin: 0 }}>{hours.toString().padStart(2, '0')}</div>
                                  {showLabels && <div className="text-sm text-gray-300">Hour</div>}
                                </div>
                                <div className="text-[24px] flex items-start" style={{ paddingTop: 0, lineHeight: 1 }}>:</div>
                                <div className="text-center flex flex-col items-center">
                                  <div className="text-[24px]" style={{ height: 'auto', lineHeight: 1, padding: 0, margin: 0 }}>{minutes.toString().padStart(2, '0')}</div>
                                  {showLabels && <div className="text-sm text-gray-300">Min</div>}
                                </div>
                                <div className="text-[24px] flex items-start" style={{ paddingTop: 0, lineHeight: 1 }}>:</div>
                                <div className="text-center flex flex-col items-center">
                                  <div className="text-[24px]" style={{ height: 'auto', lineHeight: 1, padding: 0, margin: 0 }}>{seconds.toString().padStart(2, '0')}</div>
                                  {showLabels && <div className="text-sm text-gray-300">Sec</div>}
                                </div>
                              </>
                            );
                          } else {
                            // Show only minutes and seconds (no days, no hours)
                            return (
                              <>
                                <div className="text-center flex flex-col items-center">
                                  <div className="text-[24px]" style={{ height: 'auto', lineHeight: 1, padding: 0, margin: 0 }}>{minutes.toString().padStart(2, '0')}</div>
                                  {showLabels && <div className="text-sm text-gray-300">Min</div>}
                                </div>
                                <div className="text-[24px] flex items-start" style={{ paddingTop: 0, lineHeight: 1 }}>:</div>
                                <div className="text-center flex flex-col items-center">
                                  <div className="text-[24px]" style={{ height: 'auto', lineHeight: 1, padding: 0, margin: 0 }}>{seconds.toString().padStart(2, '0')}</div>
                                  {showLabels && <div className="text-sm text-gray-300">Sec</div>}
                                </div>
                              </>
                            );
                          }
                        })()}
                      </div>
                    ) : (
                      <div className="text-gray-400 text-sm">Countdown Hidden</div>
                    )}
                  </div>
                );
              })()}
              {/* Status Bar - Matches width of black and red bars */}
              {/* Hide status bar when DEFAULT - show only clock */}
              {data.controlState?.countdownState && data.controlState?.countdownState !== 'DEFAULT' ? (
                <div className="w-full flex items-center justify-center">
                  {/* Status Bar - Shows state below countdown */}
                  {data.controlState?.primaryCountdownVisible && (
                    <div className={`text-sm font-bold text-center w-full flex items-center justify-center py-1 ${isEmergencyMode
                      ? 'bg-[#2a2a2a] text-white'
                      : data.controlState?.countdownState === 'GO'
                        ? 'bg-green-600 text-white'
                        : data.controlState?.countdownState === 'SCRUB'
                          ? 'bg-red-600 text-white'
                          : data.controlState?.countdownState === 'HOLD'
                            ? 'bg-orange-600 text-white'
                            : data.controlState?.countdownState === 'AWAITING_UPDATE'
                              ? 'bg-yellow-600 text-black'
                              : 'bg-gray-600 text-white'
                      }`}>
                      {data.controlState?.countdownState === 'GO'
                        ? 'GO FOR LAUNCH'
                        : data.controlState?.countdownState === 'AWAITING_UPDATE'
                          ? 'AWAITING UPDATE'
                          : data.controlState?.countdownState === 'HOLD'
                            ? 'HOLD'
                            : data.controlState?.countdownState === 'SCRUB'
                              ? 'SCRUB'
                              : data.controlState?.countdownState?.toUpperCase()
                      }
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Active Popups - Host/Crew/Social Media Popup - Under Primary Clock Box - Hidden in emergency mode */}
      {!isEmergencyMode && data.activePopups && data.activePopups.length > 0 && (
        <div className="absolute right-0 z-10" style={{
          top: (() => {
            const hasStatusBar = data.controlState?.countdownState && data.controlState?.countdownState !== 'DEFAULT';
            // Header bar: ~36px, Countdown box: ~60-70px, Status bar: ~28px when visible
            const headerHeight = 36;
            const countdownBoxHeight = data.controlState?.showDays ? 70 : 60;
            const statusBarHeight = hasStatusBar ? 28 : 0;
            const spacing = 8; // Small gap between elements
            return `${16 + headerHeight + countdownBoxHeight + statusBarHeight + spacing}px`;
          })(),
          width: '211px' // Match the LIVE/time box width
        }}>
          {data.activePopups.map((popup, index) => {
            const isCurrentPopup = popup.id === currentPopupId;
            const shouldShow = popupVisible && !superchatVisible && isCurrentPopup;

            return (
              <div
                key={popup.id}
                className={`bg-black bg-opacity-75 text-white relative flex overflow-hidden ${shouldShow
                  ? 'translate-x-0 opacity-100'
                  : 'translate-x-full opacity-0'
                  }`}
                style={{
                  width: '100%',
                  minHeight: 'auto',
                  height: 'fit-content',
                  pointerEvents: shouldShow ? 'auto' : 'none',
                  transition: 'transform 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                }}
              >
                {/* Red bar on left edge - pushes in from right to left */}
                <div className={`${isEmergencyMode ? 'bg-[#2a2a2a]' : 'bg-[#a20000]'} flex-shrink-0 ${shouldShow
                  ? 'w-[4px]'
                  : 'w-0'
                  }`} style={{
                    transform: shouldShow ? 'translateX(0)' : 'translateX(-4px)',
                    transition: 'width 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                  }}></div>

                {/* Content */}
                <div className="flex items-center px-3 py-1 flex-1">
                  {/* Profile Picture */}
                  <div className="flex-shrink-0 mr-3">
                    {popup.imageUrl ? (
                      <img
                        src={popup.imageUrl}
                        alt={popup.name}
                        className="w-16 h-16 object-cover"
                        loading="lazy"
                        decoding="async"
                        width="64"
                        height="64"
                        style={{
                          backgroundColor: '#374151',
                          transition: 'opacity 0.3s ease-in-out'
                        }}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/64';
                          e.target.onerror = null; // Prevent infinite loop
                        }}
                        onLoad={(e) => {
                          e.target.style.opacity = '1';
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-700 border border-gray-600 flex items-center justify-center">
                        <span className="text-white text-xs">IMG</span>
                      </div>
                    )}
                  </div>

                  {/* Text Content */}
                  <div className="flex flex-col flex-1" style={{ gap: 0 }}>
                    {/* Name - Bold, uppercase */}
                    <div className="text-white text-sm font-bold uppercase tracking-wide" style={{ lineHeight: 1.2, margin: 0, padding: 0 }}>
                      {popup.name}
                    </div>
                    {/* Title and Description - Close together */}
                    <div style={{ lineHeight: 1.1 }}>
                      {popup.title && (
                        <div className="text-white text-sm font-normal" style={{ lineHeight: 1.1, margin: 0, padding: 0, marginTop: '2px' }}>
                          {popup.title}
                        </div>
                      )}
                      {popup.description && (
                        <div className="text-white text-sm font-normal" style={{ lineHeight: 1.1, margin: 0, padding: 0, marginTop: '2px' }}>
                          {popup.description}
                        </div>
                      )}
                    </div>
                    {/* Username - With gap from description */}
                    {popup.username && (
                      <div className="text-white text-sm font-normal" style={{ marginTop: '8px' }}>
                        {popup.username}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Superchat Display - YouTube Chat/Superchat */}
      {(() => {
        const currentComment = data?.comments?.[currentCommentIndex];
        const isSuperchat = currentComment?.amountMicros && currentComment.amountMicros > 0;
        const hasSuperchat = data?.comments && data.comments.length > 0 &&
          data.controlState?.commentsVisible &&
          data.controlState?.chatConnected &&
          isSuperchat;
        const shouldShow = hasSuperchat && superchatVisible;

        // Always render container when there's a superchat available, so transitions can complete
        if (!hasSuperchat) return null;

        // Format amount from micros (e.g., 420690000 micros = $420.69)
        const formatAmount = (micros, currency = 'USD') => {
          const amount = micros / 1000000;
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(amount);
        };

        const amount = formatAmount(currentComment.amountMicros, currentComment.currency || 'USD');
        const donorName = currentComment.authorName || 'Anonymous';
        const commentText = currentComment.commentText || '';

        return (
          <div className="absolute right-0 z-10" style={{
            top: (() => {
              const hasStatusBar = data.controlState?.countdownState && data.controlState?.countdownState !== 'DEFAULT';
              // Header bar: ~36px, Countdown box: ~60-70px, Status bar: ~28px when visible
              const headerHeight = 36;
              const countdownBoxHeight = data.controlState?.showDays ? 70 : 60;
              const statusBarHeight = hasStatusBar ? 28 : 0;
              const spacing = 8; // Small gap between elements
              return `${16 + headerHeight + countdownBoxHeight + statusBarHeight + spacing}px`;
            })(),
            width: '211px' // Match the LIVE/time box width (same as popup)
          }}>
            <div className={`bg-black bg-opacity-75 text-white relative flex overflow-hidden ${shouldShow
              ? 'translate-x-0 opacity-100'
              : 'translate-x-full opacity-0'
              }`} style={{
                width: '100%',
                minHeight: 'auto',
                height: 'fit-content',
                pointerEvents: shouldShow ? 'auto' : 'none',
                transition: 'transform 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              }}>
              {/* Red bar on left edge - pushes in from right to left */}
              <div className={`bg-[#a20000] flex-shrink-0 ${shouldShow
                ? 'w-[4px]'
                : 'w-0'
                }`} style={{
                  transform: shouldShow ? 'translateX(0)' : 'translateX(-4px)',
                  transition: 'width 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                }}></div>

              {/* Content */}
              <div className="px-3 py-2 flex flex-col flex-1" style={{ maxWidth: 'calc(100% - 4px)' }}>
                {/* Donor name and amount */}
                <div className="text-sm font-medium whitespace-nowrap">
                  {donorName} - {amount}
                </div>
                {/* Comment text - can wrap to second line if needed */}
                {commentText && (
                  <div className="text-sm text-white break-words whitespace-normal" style={{ wordWrap: 'break-word', maxWidth: '100%' }}>
                    {commentText}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Regular Comments Display - Non-Superchat Comments - Hidden in emergency mode (doesn't touch edges) */}
      {!isEmergencyMode && (() => {
        const currentComment = data?.comments?.[currentCommentIndex];
        const isSuperchat = currentComment?.amountMicros && currentComment.amountMicros > 0;
        const hasRegularComment = data?.comments && data.comments.length > 0 &&
          data.controlState?.commentsVisible &&
          data.controlState?.chatConnected &&
          !isSuperchat &&
          currentComment;
        const shouldShowComment = hasRegularComment && commentVisible && !superchatVisible && !popupVisible;

        // Debug logging
        if (data?.comments && data.comments.length > 0) {
          console.log('📝 Comment Display Debug:', {
            totalComments: data.comments.length,
            currentIndex: currentCommentIndex,
            currentComment: currentComment,
            isSuperchat,
            hasRegularComment,
            commentVisible,
            superchatVisible,
            popupVisible,
            shouldShowComment,
            commentsVisible: data.controlState?.commentsVisible,
            chatConnected: data.controlState?.chatConnected
          });
        }

        // Always render container when there's a regular comment available, so transitions can complete
        if (!hasRegularComment) return null;

        const authorName = currentComment.authorName || 'Anonymous';
        const commentText = currentComment.commentText || '';

        return (
          <div className="absolute right-0 z-10" style={{
            top: (() => {
              const hasStatusBar = data.controlState?.countdownState && data.controlState?.countdownState !== 'DEFAULT';
              // Header bar: ~36px, Countdown box: ~60-70px, Status bar: ~28px when visible
              const headerHeight = 36;
              const countdownBoxHeight = data.controlState?.showDays ? 70 : 60;
              const statusBarHeight = hasStatusBar ? 28 : 0;
              const spacing = 8; // Small gap between elements
              return `${16 + headerHeight + countdownBoxHeight + statusBarHeight + spacing}px`;
            })(),
            width: '211px' // Match the LIVE/time box width (same as popup)
          }}>
            <div className={`bg-black bg-opacity-75 text-white relative flex overflow-hidden ${shouldShowComment
              ? 'translate-x-0 opacity-100'
              : 'translate-x-full opacity-0'
              }`} style={{
                width: '100%',
                minHeight: 'auto',
                height: 'fit-content',
                pointerEvents: shouldShowComment ? 'auto' : 'none',
                transition: 'transform 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              }}>
              {/* Red bar on left edge - pushes in from right to left */}
              <div className={`bg-[#a20000] flex-shrink-0 ${shouldShowComment
                ? 'w-[4px]'
                : 'w-0'
                }`} style={{
                  transform: shouldShowComment ? 'translateX(0)' : 'translateX(-4px)',
                  transition: 'width 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                }}></div>

              {/* Content */}
              <div className="px-3 py-2 flex flex-col flex-1" style={{ maxWidth: 'calc(100% - 4px)' }}>
                {/* Author name */}
                <div className="text-sm font-medium whitespace-nowrap">
                  {authorName}
                </div>
                {/* Comment text - can wrap to multiple lines if needed */}
                {commentText && (
                  <div className="text-sm text-white break-words whitespace-normal mt-1" style={{ wordWrap: 'break-word', maxWidth: '100%' }}>
                    {commentText}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Bottom - News Ticker Banner - Visible in emergency mode (touches bottom edge) */}
      {(!isEmergencyMode || true) && data.controlState?.memberBarVisible !== false && (
        <div className={`absolute bottom-0 left-0 right-0 z-10 ${isEmergencyMode ? 'opacity-50' : ''}`}>
          <div className="bg-black px-4  text-white text-xl relative" >
            {/* Continuous member scroll behind everything - Only show if enabled */}
            {data.controlState?.memberScrollVisible !== false && (
              <div className="absolute inset-0 flex items-center overflow-hidden">
                {/* Left fade gradient */}
                <div className="absolute left-0 top-0 bottom-0 w-16 pointer-events-none z-10" style={{
                  background: 'linear-gradient(to right, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0))'
                }}></div>
                {/* Right fade gradient */}
                <div className="absolute right-0 top-0 bottom-0 w-16 pointer-events-none z-10" style={{
                  background: 'linear-gradient(to left, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0))'
                }}></div>
                <div
                  className="flex text-white whitespace-nowrap relative z-0"
                  style={{
                    animation: `scroll-infinite ${scrollDuration}s linear infinite`
                  }}
                >
                  {memberScroll.length > 0 ? (
                    <>
                      {/* Create truly infinite content with duplicate sets for seamless flow */}
                      {Array.from({ length: 2 }, (_, setIndex) =>
                        Array.from({ length: 50 }, (_, repIndex) =>
                          memberScroll.map((member, index) => (
                            <span key={`set-${setIndex}-rep-${repIndex}-${member.id}`} className="flex-shrink-0">
                              {member.name}
                              <span className="mx-2">|</span>
                            </span>
                          ))
                        ).flat()
                      )}
                    </>
                  ) : (
                    <>
                      {/* Default message with truly infinite repetition */}
                      {Array.from({ length: 2 }, (_, setIndex) =>
                        Array.from({ length: 50 }, (_, repIndex) => (
                          <span key={`set-${setIndex}-no-members-${repIndex}`} className="flex-shrink-0">
                            No members added yet
                            <span className="mx-1"> </span>
                          </span>
                        ))
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Central red banner with rotating text - positioned above the scroll */}
            <div className="relative z-10 flex justify-center">
              {/* Left fade gradient for banner - fades text as it approaches banner */}
              <div className="absolute top-0 bottom-0 pointer-events-none" style={{
                left: 'calc(50% - 220px)',
                width: '100px',
                background: 'linear-gradient(to right, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1))'
              }}></div>
              <div className="absolute top-0 bottom-0 pointer-events-none" style={{
                left: 'calc(50% + 160px)',
                width: '100px',
                background: 'linear-gradient(to left, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1))'
              }}></div>
              <div className={`relative ${isEmergencyMode ? 'bg-[#2a2a2a]' : 'bg-[#a20000]'} w-80 h-8 flex items-center justify-center`} style={{
                boxShadow: '-10px 0 20px rgba(0, 0, 0, 0.5), 10px 0 20px rgba(0, 0, 0, 0.5)'
              }}>
                {/* Left fade gradient */}
                <div className="absolute left-0 top-0 bottom-0 w-16 pointer-events-none" style={{
                  background: 'linear-gradient(to right, rgba(162, 0, 0, 1), rgba(162, 0, 0, 0))'
                }}></div>
                {/* Right fade gradient */}
                <div className="absolute right-0 top-0 bottom-0 w-16 pointer-events-none" style={{
                  background: 'linear-gradient(to left, rgba(162, 0, 0, 1), rgba(162, 0, 0, 0))'
                }}></div>
                <span className={`relative z-10 text-white font-nasalization text-center transition-opacity duration-500 ${textFade ? 'opacity-0' : 'opacity-100'
                  }`}>
                  {(() => {
                    const rotatingTexts = [
                      'The Launch Pad',
                      'tlpnetwork.com',
                      'Space Is Better Together'
                    ];
                    const text = rotatingTexts[rotatingTextIndex];
                    // Display tlpnetwork.com in lowercase, others in uppercase
                    return rotatingTextIndex === 1 ? text : text.toUpperCase();
                  })()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Left Container - Stacked Sections */}
      <div className={`absolute left-6 right-6 z-10 space-y-2 ${data.controlState?.memberBarVisible !== false ? 'bottom-12' : 'bottom-12'}`}>




        {/* Update Banner & Headline - Hidden in emergency mode (doesn't touch edges) */}
        {!isEmergencyMode && (() => {
          const shouldShowHeadlines = data.overlay && (data.overlay.headline1 || data.overlay.subHeadline) &&
            (data.controlState?.headerVisible !== false || data.controlState?.subheaderVisible !== false);

          return shouldShowHeadlines;
        })() && (
            <div className={`${data.comments && data.comments.length > 0 && data.controlState?.commentsVisible && data.controlState?.chatConnected ? 'pb-2 w-fit' : 'pb-6 w-fit'}`}>

              {/* // <div className= pb-10"> */}
              {/* Update Type Banner - Now has its own toggle */}
              {data.overlay.updateType && data.controlState?.updateBannerVisible && (
                <div
                  className={`${isEmergencyMode ? 'bg-[#2a2a2a]' : 'bg-[#a20000]'} px-3 py-1 text-white font-bold text-sm w-fit overflow-hidden ${updateBannerVisible
                    ? 'translate-x-0 opacity-100'
                    : '-translate-x-full opacity-0'
                    }`}
                  style={{
                    pointerEvents: updateBannerVisible ? 'auto' : 'none',
                    transition: 'transform 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                  }}
                >
                  {data.overlay.updateType.replace(/_/g, ' ').toUpperCase()}
                </div>
              )}

              {/* Headline and Sub-Headline Section */}
              {(data.overlay.headline1 && data.controlState?.headerVisible !== false) || (data.overlay.subHeadline && data.controlState?.subheaderVisible !== false) ? (
                <div
                  className={`bg-black bg-opacity-75 text-white relative flex pl-2 py-2 overflow-hidden ${headlineVisible
                    ? 'translate-x-0 opacity-100'
                    : '-translate-x-full opacity-0'
                    }`}
                  style={{
                    pointerEvents: headlineVisible ? 'auto' : 'none',
                    transition: 'transform 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                  }}
                >
                  {/* Vertical white line on the left */}
                  <div className={`${isEmergencyMode ? 'bg-[#2a2a2a]' : 'bg-white'} flex-shrink-0 absolute left-2 top-2 bottom-2 transition-all duration-1200`} style={{
                    width: headlineVisible ? '2px' : '0px',
                    transform: headlineVisible ? 'translateX(0)' : 'translateX(-2px)'
                  }}></div>
                  {/* Content container */}
                  <div className="flex-1 pr-4 ml-2" style={{ paddingLeft: '2px' }}>
                    {/* Headline */}
                    {data.overlay.headline1 && data.controlState?.headerVisible !== false && (
                      <div className="text-xl font-bold uppercase mb-1">
                        {data.overlay.headline1.replace(/_/g, ' ')}
                      </div>
                    )}
                    {/* Sub-Headline */}
                    {data.overlay.subHeadline && data.controlState?.subheaderVisible !== false && (
                      <div className="text-sm font-normal">
                        {data.overlay.subHeadline.replace(/_/g, ' ')}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          )}


        {/* Horizontal Events View - Animated Timeline */}
        {data.controlState?.eventsViewType === 'horizontal' && (() => {
          // Get all events from database
          const allEvents = data.events || [];

          // Get visible events
          const visibleEvents = allEvents.filter(event => event.isVisible !== false);

          // Calculate current time for animation
          const now = currentTime;
          const secondsElapsed = Math.floor(now.getTime() / 1000) % 1000;

          return (
            <div className="w-full -mb-2">
              <div className="px-">
                {/* Up Next Banner */}
                <div className="bg-[#a20000] px-3 py-1 text-white text-sm font-bold mb-1 w-fit">
                  UP NEXT: {(() => {
                    // Find the next upcoming event using proper time calculation
                    const now = currentTime;
                    const t0Time = new Date(data.overlay.t0Timestamp);
                    const streamDelay = data.controlState?.streamDelay || 0;
                    const adjustedT0Time = new Date(t0Time.getTime() + (streamDelay * 1000));

                    let nextEvent = null;
                    let minRemainingSeconds = Infinity;

                    // Check each event to find the next one
                    visibleEvents.forEach(event => {
                      const eventTime = new Date(adjustedT0Time.getTime() + (event.timeOffset * 1000));

                      // If status is not GO and not DEFAULT, pause the countdown by using the paused time
                      let diffMs;
                      if (data.controlState?.countdownState && data.controlState?.countdownState !== 'GO' && data.controlState?.countdownState !== 'DEFAULT') {
                        const pausedAt = data.controlState?.countdownPausedAt ? new Date(data.controlState.countdownPausedAt) : now;
                        const pausedDuration = data.controlState?.countdownPausedDuration || 0;
                        const originalDiffMs = eventTime.getTime() - pausedAt.getTime();
                        diffMs = originalDiffMs + (pausedDuration * 1000);
                      } else {
                        diffMs = eventTime.getTime() - now.getTime();
                      }

                      const remainingSeconds = Math.floor(diffMs / 1000);

                      // Find the next upcoming event (positive remaining seconds)
                      if (remainingSeconds > 0 && remainingSeconds < minRemainingSeconds) {
                        minRemainingSeconds = remainingSeconds;
                        nextEvent = event;
                      }
                    });

                    if (nextEvent) {
                      return `${nextEvent.eventName} ${displayEventTime(nextEvent)}`;
                    }

                    return visibleEvents.length > 0 ? 'No upcoming events' : 'No events configured';
                  })()}
                </div>

                {/* Timeline Container */}
                <div className="relative w-full h-28 overflow-hidden">
                  {/* Timeline Background Line */}
                  <div className="absolute top-10 left-0 right-0 h-0.5 bg-red-500"></div>

                  {/* T-0 Center Line */}
                  <div className="absolute top-1 left-1/2 transform -translate-x-1/2 flex flex-col items-center z-10">
                    <div className="text-white text-xs mb-1 font-bold">T-0</div>
                    <div className="w-0.5 h-8 bg-red-500"></div>
                  </div>

                  {/* Events - Animated positioning */}
                  {visibleEvents.length > 0 ? (() => {
                    // === DYNAMIC GLOBAL SCALING (FIX) ===
                    // Calculate max future and past hours across ALL events first
                    let maxFutureHours = 0;
                    let maxPastHours = 0;

                    visibleEvents.forEach(event => {
                      const timeOffset = event.timeOffset || 0;
                      const now = currentTime;
                      const t0Time = new Date(data.overlay.t0Timestamp);
                      const streamDelay = data.controlState?.streamDelay || 0;
                      const adjustedT0Time = new Date(t0Time.getTime() + (streamDelay * 1000));
                      const eventTime = new Date(adjustedT0Time.getTime() + (timeOffset * 1000));

                      let diffMs;
                      if (data.controlState?.countdownState && data.controlState?.countdownState !== 'GO' && data.controlState?.countdownState !== 'DEFAULT') {
                        const pausedAt = data.controlState?.countdownPausedAt ? new Date(data.controlState.countdownPausedAt) : now;
                        const pausedDuration = data.controlState?.countdownPausedDuration || 0;
                        const originalDiffMs = eventTime.getTime() - pausedAt.getTime();
                        diffMs = originalDiffMs + (pausedDuration * 1000);
                      } else {
                        diffMs = eventTime.getTime() - now.getTime();
                      }

                      const remainingSeconds = diffMs / 1000; // Keep fractional seconds!
                      const absHours = Math.abs(remainingSeconds) / 3600;

                      if (remainingSeconds > 0) {
                        maxFutureHours = Math.max(maxFutureHours, absHours);
                      } else {
                        maxPastHours = Math.max(maxPastHours, absHours);
                      }
                    });

                    const timeScaleFuture = 45 / (maxFutureHours || 1);
                    const timeScalePast = 45 / (maxPastHours || 1);
                    // =====================================

                    return visibleEvents.map((event, index) => {
                      const timeOffset = event.timeOffset || 0;

                      // Calculate actual event time and remaining time
                      const now = currentTime;
                      const t0Time = new Date(data.overlay.t0Timestamp);

                      // Apply stream delay to T-0 time (add delay seconds to T-0)
                      const streamDelay = data.controlState?.streamDelay || 0;
                      const adjustedT0Time = new Date(t0Time.getTime() + (streamDelay * 1000));

                      const eventTime = new Date(adjustedT0Time.getTime() + (timeOffset * 1000));

                      // If status is not GO and not DEFAULT, pause the countdown by using the paused time
                      let diffMs;
                      if (data.controlState?.countdownState && data.controlState?.countdownState !== 'GO' && data.controlState?.countdownState !== 'DEFAULT') {
                        // Use the paused time from control state if available
                        const pausedAt = data.controlState?.countdownPausedAt ? new Date(data.controlState.countdownPausedAt) : now;
                        const pausedDuration = data.controlState?.countdownPausedDuration || 0;

                        // Calculate time difference from when it was paused
                        const originalDiffMs = eventTime.getTime() - pausedAt.getTime();
                        diffMs = originalDiffMs + (pausedDuration * 1000);
                      } else {
                        // Normal calculation for GO state
                        diffMs = eventTime.getTime() - now.getTime();
                      }

                      const remainingSeconds = diffMs / 1000; // Keep fractional seconds for smooth movement!

                      // Calculate position: Events flow from right to left based on actual time
                      // T-0 is at center (50%), events positioned by their actual time distance
                      const rightEdgePosition = 95; // Start from far right edge (95% from left)
                      const centerPosition = 50; // T-0 line (50% from left)
                      const leftEdgePosition = 5; // Far left edge (5% from left)

                      // Calculate position based on actual time flow using GLOBAL scaling
                      let eventPosition;

                      if (remainingSeconds > 0) {
                        // Event is in the future - position based on actual time remaining
                        const hoursRemaining = remainingSeconds / 3600; // Convert to hours
                        const positionFromCenter = (hoursRemaining * timeScaleFuture); // Use global future scale

                        // Position from center, but don't go beyond right edge
                        eventPosition = Math.min(centerPosition + positionFromCenter, rightEdgePosition);
                      } else if (Math.abs(remainingSeconds) <= 0.5) {
                        // Event is exactly at T-0 (within 0.5 seconds)
                        eventPosition = centerPosition;
                      } else {
                        // Event has passed T-0, position based on how long ago it passed
                        const hoursPast = Math.abs(remainingSeconds) / 3600; // Convert to hours
                        const positionFromCenter = (hoursPast * timeScalePast); // Use global past scale

                        // Position to the left of center, allow going off screen
                        eventPosition = centerPosition - positionFromCenter;
                      }

                      // Allow events to move off screen to the left (no left boundary)
                      // Only constrain the right boundary to prevent events from starting off screen
                      eventPosition = Math.min(95, eventPosition);

                      // Calculate distance from T-0 for visual effects
                      const distanceFromT0 = Math.abs(remainingSeconds);
                      const proximityToT0 = 1 - (distanceFromT0 / (Math.abs(timeOffset) || 1)); // 0 to 1, higher = closer to T-0

                      // Determine event state based on proximity to T-0 and position
                      const isAtT0 = Math.abs(remainingSeconds) < 1; // Exactly at T-0
                      const isNearT0 = distanceFromT0 < 30; // Within 30 seconds of T-0
                      const isFarFromT0 = distanceFromT0 > 60; // More than 60 seconds from T-0
                      const isPast = remainingSeconds < 0;
                      const isFuture = remainingSeconds > 0;

                      // Additional visual states based on position
                      const isAtRightEdge = eventPosition > 85; // Near right edge
                      const isAtLeftEdge = eventPosition < 15; // Near left edge
                      const isAtCenter = Math.abs(eventPosition - centerPosition) < 5; // Near T-0 line
                      const isOffScreen = eventPosition < -10; // Completely off screen to the left

                      // Enhanced past event detection - if event is on left side, it's past
                      const isPastByPosition = eventPosition < centerPosition - 10; // Left of T-0 line
                      const isDefinitelyPast = isPast || isPastByPosition;

                      // Determine if event name should be above or below the line (alternating)
                      const isAboveLine = index % 2 === 0;

                      return (
                        <div
                          key={event.id}
                          className="absolute flex flex-col items-center cursor-pointer transition-all duration-1000 ease-linear"
                          style={{
                            left: `${eventPosition}%`,
                            top: '10px',
                            transform: 'translateX(-50%)',
                            zIndex: isAtT0 ? 30 : isNearT0 ? 20 : isAtRightEdge ? 5 : 10,
                            opacity: 1
                          }}
                          title={`${event.eventName} - ${remainingSeconds > 0
                            ? `T-${Math.floor(remainingSeconds)}`
                            : remainingSeconds >= -0.5 && remainingSeconds <= 0.5
                              ? 'T-0'
                              : `T+${Math.floor(Math.abs(remainingSeconds))}`
                            }`}
                        >
                          {/* Event Label */}
                          <div
                            className={`text-xs text-center whitespace-nowrap transition-all duration-500 absolute ${isAtT0
                              ? 'text-red-400 font-bold scale-125'
                              : isDefinitelyPast
                                ? 'text-red-600 font-bold scale-110'
                                : isNearT0
                                  ? 'text-orange-300 font-semibold scale-110'
                                  : 'text-white'
                              }`}
                            style={{
                              color: isAtT0 ? '#f87171' : isDefinitelyPast ? '#dc2626' : isNearT0 ? `rgba(251, 146, 60, ${0.6 + proximityToT0 * 0.4})` : '#ffffff',
                              top: '-11px',
                              left: '50%',
                              transform: 'translateX(-50%)'
                            }}
                          >
                            {event.eventName}
                          </div>

                          {/* Event Marker - Positioned on Timeline */}
                          <div
                            className={`w-5 h-5 rounded-full border-4 transition-all duration-500 absolute ${isAtT0
                              ? 'bg-red-500 border-red-500 shadow-lg shadow-red-500/50 scale-150'
                              : isDefinitelyPast
                                ? 'bg-red-700 border-red-700 shadow-lg shadow-red-700/50 scale-110'
                                : isNearT0
                                  ? 'bg-orange-400 border-orange-400 shadow-md shadow-orange-400/30 scale-110'
                                  : 'bg-white border-black shadow-xl shadow-white/100'
                              }`}
                            style={{
                              backgroundColor: isAtT0 ? '#ef4444' : isDefinitelyPast ? '#b91c1c' : isNearT0 ? `rgba(251, 146, 60, ${0.5 + proximityToT0 * 0.5})` : '#ffffff',
                              borderColor: isAtT0 ? '#ef4444' : isDefinitelyPast ? '#b91c1c' : isNearT0 ? `rgba(251, 146, 60, ${0.5 + proximityToT0 * 0.5})` : '#000000',
                              opacity: 1,
                              boxShadow: isAtT0 ? '0 0 20px rgba(239, 68, 68, 0.8)' : isDefinitelyPast ? '0 0 20px rgba(185, 28, 28, 0.8)' : isNearT0 ? '0 0 15px rgba(251, 146, 60, 0.6)' : '0 0 25px rgba(255, 255, 255, 1)',
                              top: '4px',
                              left: '50%',
                              transform: 'translateX(-50%)'
                            }}
                          >
                            {isAtT0 ? (
                              <div className="w-2 h-2 bg-white rounded-full m-0.5 animate-pulse"></div>
                            ) : isNearT0 ? (
                              <div className="w-2 h-2 bg-white rounded-full m-0.5 animate-pulse"></div>
                            ) : (
                              <div className="w-2 h-2 bg-black rounded-full m-0.5"></div>
                            )}
                          </div>

                          {/* Connecting Line to Timeline - Below the dot */}
                          <div
                            className="w-0.5 transition-all duration-500 absolute"
                            style={{
                              height: '8px',
                              backgroundColor: isAtT0 ? '#ef4444' : isDefinitelyPast ? '#b91c1c' : isNearT0 ? `rgba(251, 146, 60, ${0.6 + proximityToT0 * 0.4})` : '#ffffff',
                              opacity: 1,
                              top: '29px', // 4px (dot top) + 20px (dot height) + 5px (gap)
                              left: '50%',
                              transform: 'translateX(-50%)'
                            }}
                          ></div>



                        </div>
                      );
                    });
                  })() : (
                    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white text-sm opacity-75">
                      No events available
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </div>



      <style jsx>{`
        .animate-scroll {
          animation: scroll-left 30s linear infinite;
        }
        @keyframes scroll-left {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #3498db;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default BroadcastOutput;